/**
 * Agent Runs list — executor invocations dispatched by rules (read-only).
 * Leads with **status** and **when it ran**, then the executor, how long it
 * took, and a one-line output summary. Foreign keys resolve to readable names
 * via `RefLink`; loading / empty / error come from the shared RunsDataTable.
 */
import { useMemo } from "react";
import { useNavigation } from "@refinedev/core";
import type { ColumnDef } from "@tanstack/react-table";
import type { AgentRun } from "@aos/shared";
import { StatusBadge } from "@/components/app/StatusBadge";
import { PageHeader } from "@/components/layout/PageHeader";
import { RefLink } from "@/components/inspector";
import { RunsDataTable } from "../_shared/RunsDataTable";
import { formatDateTime } from "../_shared/runs-ui";

/** Human run duration from two ISO timestamps; em-dash when still running. */
function runDuration(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  if (!start || !end) return "—";
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (Number.isNaN(ms) || ms < 0) return "—";
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(s < 10 ? 1 : 0)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s % 60)}s`;
}

export function AgentRunList() {
  const { show } = useNavigation();

  const columns = useMemo<ColumnDef<AgentRun>[]>(
    () => [
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Started",
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {formatDateTime(row.original.started_at ?? row.original.created_at)}
          </span>
        ),
      },
      {
        id: "duration",
        header: "Duration",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {runDuration(
              row.original.started_at ?? row.original.created_at,
              row.original.finished_at,
            )}
          </span>
        ),
      },
      {
        id: "executor_type",
        accessorKey: "executor_type",
        header: "Executor",
        cell: ({ row }) => (
          <span className="font-medium">{row.original.executor_type}</span>
        ),
      },
      {
        id: "skill_id",
        accessorKey: "skill_id",
        header: "Skill",
        enableSorting: false,
        cell: ({ row }) => (
          <span onClick={(e) => e.stopPropagation()}>
            <RefLink resource="skills" id={row.original.skill_id} />
          </span>
        ),
      },
      {
        id: "output_summary",
        accessorKey: "output_summary",
        header: "Summary",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="block max-w-[20rem] truncate text-muted-foreground">
            {row.original.output_summary ?? "—"}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Agent Runs"
        subtitle="Executor invocations dispatched by rules. Open one for its full trace."
        icon="agentRuns"
      />
      <RunsDataTable<AgentRun>
        resource="agent-runs"
        columns={columns}
        onRowClick={(record) => show("agent-runs", record.id)}
        emptyMessage="No agent runs yet."
      />
    </div>
  );
}

export default AgentRunList;
